import { useMemo } from 'react';
import { useSkills } from '../../onboarding/apis/skill-registry';
import { skillRegistry } from '../../onboarding/registry/SkillRegistry';
import { useUserProfile } from '../../../api/profile';
import '../../onboarding/registry/registerSkills';

export interface EnabledTicketingTool {
  id: string;
  name: string;
}

interface EnabledTicketingToolsState {
  enabledTicketingTools: EnabledTicketingTool[];
  isLoading: boolean;
}

export const useEnabledTicketingTools = (): EnabledTicketingToolsState => {
  const { data: skills, isLoading: isLoadingSkills } = useSkills();
  const { data: userProfile } = useUserProfile();
  const userOrgId = userProfile?.organizationId;

  const enabledTicketingTools = useMemo(() => {
    if (isLoadingSkills || !skills) {
      return [];
    }

    const uiSkills = skillRegistry.getAllSkills();
    const ticketingConfigs = uiSkills.filter(
      (skill) => skill.category === 'Ticketing',
    );

    const isAlwaysEnabledForOrg = (skillConfig: (typeof uiSkills)[0]) => {
      if (!Array.isArray(skillConfig.alwaysEnabledForOrgs)) {
        return false;
      }

      return (
        skillConfig.alwaysEnabledForOrgs.includes('*') ||
        skillConfig.alwaysEnabledForOrgs.includes(userOrgId ?? '')
      );
    };

    const findSkillState = (registryId: string) =>
      skills.find(
        (skill) =>
          skill.skillType === registryId ||
          skill.skillType.toLowerCase() === registryId.toLowerCase(),
      );

    return ticketingConfigs
      .filter((skillConfig) => {
        if (isAlwaysEnabledForOrg(skillConfig)) {
          return true;
        }

        return findSkillState(skillConfig.id)?.enabled ?? false;
      })
      .map((skillConfig) => ({
        id: skillConfig.id,
        name: skillConfig.name,
      }));
  }, [skills, isLoadingSkills, userOrgId]);

  return {
    enabledTicketingTools,
    isLoading: isLoadingSkills,
  };
};
